import {
    AIRPORT_CODE_PATTERN,
    CEILING_UNKNOWN_TEXT,
    CEILING_ZERO_TEXT,
    LAANC_BODY_TEXT,
    LAANC_GRID_LAYER,
    LAANC_SUBTITLE_TEXT
} from './constants';
import type { AirspaceRestriction, CardContent, CeilingDisplay } from './types';

const METERS_PER_MILE = 1609.344;
const METERS_PER_FOOT = 0.3048;
const FEET_ROUND_STEP = 10;
const MILE_THRESHOLD_METERS = 160;

const formatDistance = (distanceM: number | null): string | null => {
    if (distanceM === null || distanceM <= 0) {
        return null;
    }

    if (distanceM < MILE_THRESHOLD_METERS) {
        const feet = distanceM / METERS_PER_FOOT;
        const rounded = Math.round(feet / FEET_ROUND_STEP) * FEET_ROUND_STEP;

        return `${rounded} ft from pin`;
    }

    const miles = distanceM / METERS_PER_MILE;

    return `${miles.toFixed(1)} mi from pin`;
};

type TitleAndCode = { title: string; airportCode: string | null };

const extractTitleAndCode = (name: string): TitleAndCode => {
    const match = name.match(AIRPORT_CODE_PATTERN);

    if (match === null) {
        return { airportCode: null, title: name };
    }

    return { airportCode: match[2], title: match[1].trim() };
};

const buildCeilingDisplay = (
    isLaanc: boolean,
    restriction: AirspaceRestriction
): CeilingDisplay | null => {
    if (!isLaanc) {
        return null;
    }

    const ceiling = restriction.upperFt;

    if (ceiling === null || ceiling === undefined) {
        console.warn('LAANC grid row missing upper_altitude_ft', restriction.sourceId);

        return { kind: 'unknown', text: CEILING_UNKNOWN_TEXT };
    }

    if (ceiling === 0) {
        return { kind: 'zero', suffixText: CEILING_ZERO_TEXT, valueText: '0 ft' };
    }

    return { kind: 'known', suffixText: 'AGL ceiling', valueText: `${ceiling} ft` };
};

const pickString = (value: unknown): string | null =>
    typeof value === 'string' && value.length > 0 ? value : null;

const SUA_TYPE_LABELS: Record<string, string> = {
    A: 'ALERT',
    MOA: 'MOA',
    P: 'PROHIBITED',
    R: 'RESTRICTED',
    W: 'WARNING'
};

// Extracts the FAA SUA type letter from backend's description prefix, e.g.
// "R -- R-5701 (Schedule: ...)" → "R". Handles MOA as a multi-char variant.
const SUA_TYPE_FROM_DESCRIPTION = /^(MOA|[APRW])\s*--/;

const CLASS_LETTER_PATTERN = /class\s+([a-g][0-9]?)/i;

const resolveCategoryLabel = (
    isLaanc: boolean,
    restriction: AirspaceRestriction
): string | null => {
    if (isLaanc) {
        return 'LAANC';
    }

    if (restriction.layer === 'sua') {
        const text = pickString(restriction.properties.full_text);
        const match = text?.match(SUA_TYPE_FROM_DESCRIPTION);

        return match ? (SUA_TYPE_LABELS[match[1]] ?? 'SPECIAL USE') : 'SPECIAL USE';
    }

    if (restriction.layer === 'prohibited_area') {
        return 'PROHIBITED';
    }

    if (restriction.layer === 'nsufr') {
        return 'NSUFR';
    }

    if (restriction.layer === 'stadium') {
        return 'STADIUM TFR';
    }

    if (restriction.layer === 'tfr') {
        return 'TFR';
    }

    if (restriction.layer.startsWith('class_')) {
        const classMatch = restriction.name.match(CLASS_LETTER_PATTERN);

        return classMatch ? `CLASS ${classMatch[1].toUpperCase()}` : 'CONTROLLED';
    }

    if (restriction.layer === 'airport_controlled') {
        return 'CONTROLLED AIRPORT';
    }

    if (restriction.layer === 'airport_uncontrolled') {
        return 'UNCONTROLLED AIRPORT';
    }

    if (restriction.layer === 'class_g') {
        return 'CLASS G';
    }

    return null;
};

const resolveSubtitle = (isLaanc: boolean, restriction: AirspaceRestriction): string | null => {
    // LAANC cards always show the short prototype copy regardless of what the backend
    // sends in `description`/`full_text` — those are verbose ("LAANC enabled — ceiling:
    // 400 ft AGL") and duplicate info the ceiling row already carries.
    if (isLaanc) {
        return LAANC_SUBTITLE_TEXT;
    }

    const explicit = pickString(restriction.properties.subtitle);

    if (explicit !== null) {
        return explicit;
    }

    return pickString(restriction.properties.full_text);
};

const resolveBody = (isLaanc: boolean, restriction: AirspaceRestriction): string | null => {
    if (isLaanc) {
        return LAANC_BODY_TEXT;
    }

    // Safe card emits its body via `full_text` and its subtitle via an explicit `subtitle` prop.
    // Every other severity currently lacks a backend-supplied body — flag in Phase 4 handoff.
    const hasExplicitSubtitle = typeof restriction.properties.subtitle === 'string';

    if (!hasExplicitSubtitle) {
        return null;
    }

    return pickString(restriction.properties.full_text);
};

export const buildCardContent = (restriction: AirspaceRestriction): CardContent => {
    const { airportCode, title } = extractTitleAndCode(restriction.name);
    const isLaanc = restriction.layer === LAANC_GRID_LAYER;

    return {
        airportCode,
        body: resolveBody(isLaanc, restriction),
        categoryLabel: resolveCategoryLabel(isLaanc, restriction),
        ceilingDisplay: buildCeilingDisplay(isLaanc, restriction),
        distanceText: formatDistance(restriction.distanceM),
        laancBadge: false,
        showLaancIcon: isLaanc && airportCode !== null,
        subtitle: resolveSubtitle(isLaanc, restriction),
        title
    };
};

export default buildCardContent;
